import { Controller } from '@hotwired/stimulus'
import flatpickr from 'flatpickr'

export default class extends Controller {
  static targets = ['input', 'shell']
  static values = {
    enableTime: { type: Boolean, default: true },
    minuteIncrement: { type: Number, default: 5 },
    theme: { type: String, default: 'admin' },
    minDate: { type: String, default: '' }
  }

  connect () {
    if (!this.hasInputTarget) return

    this.boundRepositionMobile = () => this.repositionMobileCalendar()
    this.mobileBackdrop = null

    const initial = this.inputTarget.value?.trim()
    const themeClass = this.themeValue === 'shop' ? 'flatpickr-shop-theme' : 'flatpickr-admin-theme'
    const options = {
      enableTime: this.enableTimeValue,
      dateFormat: this.enableTimeValue ? 'Y-m-d H:i' : 'Y-m-d',
      altInput: true,
      altFormat: this.enableTimeValue ? 'D, M j, Y h:i K' : 'D, M j, Y',
      minuteIncrement: this.minuteIncrementValue,
      time_24hr: false,
      disableMobile: true,
      defaultDate: initial || null,
      allowInput: true,
      clickOpens: true,
      // Date-only: close after day. Datetime: stay open for time tweaks, close via Done.
      closeOnSelect: !this.enableTimeValue,
      position: 'auto left',
      onReady: (_selectedDates, _dateStr, instance) => {
        instance.calendarContainer.classList.add(themeClass)
        this.decorateAltInput(instance)
        this.attachConfirmControls(instance)
      },
      onChange: () => {
        this.inputTarget.dispatchEvent(new Event('input', { bubbles: true }))
        this.inputTarget.dispatchEvent(new Event('change', { bubbles: true }))
      },
      onOpen: (_selectedDates, _dateStr, instance) => {
        this.applyEffectiveMinDate(instance)
        this.openMobileCalendar(instance)
      },
      onClose: () => {
        this.closeMobileCalendar()
        this.inputTarget.dispatchEvent(new Event('input', { bubbles: true }))
        this.inputTarget.dispatchEvent(new Event('change', { bubbles: true }))
      }
    }

    if (this.minDateValue === 'now') {
      options.minDate = new Date()
    } else if (this.minDateValue) {
      options.minDate = this.minDateValue
    }

    this.externalMinDate = null
    this.picker = flatpickr(this.inputTarget, options)
  }

  disconnect () {
    this.teardown()
  }

  teardown () {
    this.closeMobileCalendar()
    if (this.picker) {
      this.picker.destroy()
      this.picker = null
    }
  }

  isMobileViewport () {
    return window.matchMedia('(max-width: 639px)').matches
  }

  openMobileCalendar (instance = this.picker) {
    if (!instance || !this.isMobileViewport()) return

    const calendar = instance.calendarContainer
    if (calendar.parentElement !== document.body) {
      document.body.appendChild(calendar)
    }

    calendar.classList.add('datetime-picker-calendar--mobile-sheet')

    if (!this.mobileBackdrop) {
      this.mobileBackdrop = document.createElement('button')
      this.mobileBackdrop.type = 'button'
      this.mobileBackdrop.className = 'datetime-picker-mobile-backdrop'
      this.mobileBackdrop.setAttribute('aria-label', 'Close calendar')
      this.mobileBackdrop.addEventListener('click', () => instance.close())
      document.body.appendChild(this.mobileBackdrop)
    }

    document.body.classList.add('datetime-picker-mobile-open')
    this.repositionMobileCalendar(instance)
    window.addEventListener('resize', this.boundRepositionMobile)
    window.addEventListener('scroll', this.boundRepositionMobile, true)
  }

  closeMobileCalendar () {
    document.body.classList.remove('datetime-picker-mobile-open')
    this.mobileBackdrop?.remove()
    this.mobileBackdrop = null
    window.removeEventListener('resize', this.boundRepositionMobile)
    window.removeEventListener('scroll', this.boundRepositionMobile, true)

    const calendar = this.picker?.calendarContainer
    if (calendar) {
      calendar.classList.remove('datetime-picker-calendar--mobile-sheet')
      calendar.style.removeProperty('position')
      calendar.style.removeProperty('left')
      calendar.style.removeProperty('top')
      calendar.style.removeProperty('bottom')
      calendar.style.removeProperty('transform')
      calendar.style.removeProperty('width')
      calendar.style.removeProperty('z-index')
    }
  }

  repositionMobileCalendar (instance = this.picker) {
    if (!instance || !this.isMobileViewport()) return

    const calendar = instance.calendarContainer
    calendar.style.position = 'fixed'
    calendar.style.left = '50%'
    calendar.style.top = 'auto'
    calendar.style.bottom = 'max(1rem, env(safe-area-inset-bottom))'
    calendar.style.transform = 'translateX(-50%)'
    calendar.style.zIndex = '10001'
    calendar.style.width = 'min(22rem, calc(100vw - 1.5rem))'
  }

  setNow (event) {
    event.preventDefault()
    if (!this.picker) return

    this.picker.setDate(new Date(), true)
  }

  clear (event) {
    event.preventDefault()
    if (!this.picker) return

    this.picker.clear()
    this.inputTarget.value = ''
    this.inputTarget.dispatchEvent(new Event('input', { bubbles: true }))
    this.inputTarget.dispatchEvent(new Event('change', { bubbles: true }))
  }

  // Allow parent controllers to raise the earliest selectable date/time.
  // Survives calendar re-open (unlike a one-off flatpickr set that "now" would overwrite).
  setMinDate (minDate) {
    if (!this.picker) return

    this.externalMinDate = this.normalizeDate(minDate)
    this.applyEffectiveMinDate(this.picker)

    const selected = this.picker.selectedDates[0]
    const min = this.effectiveMinDate()
    if (selected && min && selected.getTime() <= min.getTime()) {
      this.picker.clear()
      this.inputTarget.dispatchEvent(new Event('input', { bubbles: true }))
      this.inputTarget.dispatchEvent(new Event('change', { bubbles: true }))
    }
  }

  effectiveMinDate () {
    const floor = this.minDateValue === 'now' ? new Date() : this.normalizeDate(this.minDateValue)
    const external = this.externalMinDate

    if (floor && external) return external.getTime() > floor.getTime() ? external : floor
    return external || floor || null
  }

  applyEffectiveMinDate (instance = this.picker) {
    if (!instance) return
    instance.set('minDate', this.effectiveMinDate())
  }

  normalizeDate (value) {
    if (value == null || value === '') return null
    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : value
    }

    const parsed = new Date(value)
    return Number.isNaN(parsed.getTime()) ? null : parsed
  }

  attachConfirmControls (instance) {
    if (!this.enableTimeValue) return
    if (instance.calendarContainer.querySelector('.datetime-picker-confirm')) return

    const bar = document.createElement('div')
    bar.className = 'datetime-picker-confirm'
    const button = document.createElement('button')
    button.type = 'button'
    button.className = 'datetime-picker-confirm-btn'
    button.textContent = 'Done'
    button.addEventListener('click', (event) => {
      event.preventDefault()
      event.stopPropagation()
      this.inputTarget.dispatchEvent(new Event('input', { bubbles: true }))
      this.inputTarget.dispatchEvent(new Event('change', { bubbles: true }))
      instance.close()
    })
    bar.appendChild(button)
    instance.calendarContainer.appendChild(bar)
  }

  decorateAltInput (instance) {
    const alt = instance.altInput
    if (!alt) return

    alt.classList.add('datetime-picker-alt-input')
    if (this.themeValue === 'shop') {
      alt.classList.add('datetime-picker-alt-input--shop')
    }
    alt.setAttribute('autocomplete', 'off')
    alt.placeholder = this.enableTimeValue ? 'Pick date & time…' : 'Pick a date…'

    const shell = this.hasShellTarget ? this.shellTarget : alt.parentElement
    const wrap = alt.closest('.flatpickr-wrapper') || alt.parentElement
    if (wrap) {
      wrap.classList.add('datetime-picker-wrap')
    }

    if (!shell || shell.querySelector('.datetime-picker-icon')) return

    shell.classList.add('datetime-picker-input-shell--ready')
    if (this.themeValue === 'shop') {
      shell.classList.add('datetime-picker-input-shell--shop')
    }

    const icon = document.createElement('button')
    icon.type = 'button'
    icon.className = this.themeValue === 'shop'
      ? 'datetime-picker-icon datetime-picker-icon--shop'
      : 'datetime-picker-icon'
    icon.setAttribute('aria-label', 'Open calendar')
    icon.tabIndex = -1
    icon.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" /></svg>'
    icon.addEventListener('click', (event) => {
      event.preventDefault()
      instance.toggle()
      alt.focus()
    })
    shell.appendChild(icon)
  }
}
